import { Divider, FormItemsGroup, Input, Module, Space, Spacing } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { isDocumentTypeInvoice } from '@features/invoices/utils/isDocumentTypeInvoice'
import { isDocumentTypeQuote } from '@features/invoices/utils/isDocumentTypeQuote'

import { useInvoiceEditorDocumentType } from '../../../../hooks/useInvoiceEditorDocumentType'
import { DueDate } from './elements/DueDate'
import { InvoiceClientReferences } from './elements/InvoiceClientReferences'
import { InvoiceDateOfSaleOrService } from './elements/InvoiceDateOfSaleOrService'
import { InvoiceFooterNote } from './elements/InvoiceFooterNote'
import { InvoiceHeader } from './elements/InvoiceHeader'
import { InvoiceIssueDate } from './elements/InvoiceIssueDate'
import { InvoiceIssuer } from './elements/InvoiceIssuer'
import { InvoiceLines } from './elements/InvoiceLines'
import { InvoiceMessage } from './elements/InvoiceMessage'
import { InvoiceReceiver } from './elements/InvoiceReceiver'
import { InvoiceSummary } from './elements/InvoiceSummary'
import { Payments } from './elements/Payments'
import { QuoteDateOfSaleOrService } from './elements/QuoteDateOfSaleOrService'
import { RecurringInvoiceSettings } from './elements/RecurringInvoiceSettings'
import * as Styled from './styles'

export const InvoiceEditorForm = (): ReactElement => {
  const { t } = useTranslation()
  const { documentType, isRecurringInvoice } = useInvoiceEditorDocumentType()

  const isQuote = isDocumentTypeQuote(documentType)
  const isInvoice = isDocumentTypeInvoice(documentType)
  const shouldDisplayPayments = isInvoice || isQuote || isRecurringInvoice

  return (
    <>
      <Module>
        <Module.SectionsGroup>
          <Module.Section>
            <InvoiceHeader />
            {isRecurringInvoice ? (
              <RecurringInvoiceSettings />
            ) : (
              <>
                <Space size="s16" />
                <Divider />
                <Space size="s16" />
              </>
            )}
            <FormItemsGroup itemsInRow={2} columnSpacing={Spacing.S32}>
              <InvoiceReceiver />
              <InvoiceIssuer />
            </FormItemsGroup>
            <Space />
            <FormItemsGroup itemsInRow={4} columnSpacing={Spacing.S32}>
              <InvoiceMessage />
              <Styled.DateFieldsWrapper>
                <FormItemsGroup itemsInRow={2} columnSpacing={Spacing.S16}>
                  <InvoiceIssueDate />
                  {isQuote ? <DueDate /> : <InvoiceDateOfSaleOrService />}
                </FormItemsGroup>
                {isQuote ? (
                  <>
                    <Space />
                    <QuoteDateOfSaleOrService />
                  </>
                ) : null}
              </Styled.DateFieldsWrapper>
            </FormItemsGroup>
            {isQuote ? null : (
              <>
                <Space />
                <FormItemsGroup itemsInRow={2} columnSpacing={Spacing.S32}>
                  <InvoiceClientReferences />
                </FormItemsGroup>
              </>
            )}
          </Module.Section>
          <Module.Section title={t('external_invoices.editor.form.field.invoice_lines.label')}>
            <InvoiceLines />
            <Space />
            <InvoiceSummary />
          </Module.Section>
          <Module.Section>
            <FormItemsGroup itemsInRow={shouldDisplayPayments ? 2 : 1} columnSpacing={Spacing.S32}>
              {shouldDisplayPayments && <Payments />}
              <InvoiceFooterNote />
            </FormItemsGroup>
          </Module.Section>
        </Module.SectionsGroup>
      </Module>
      {/* ID field is used with invoice drafts */}
      <Input name="id" hidden />
    </>
  )
}
